// src/Admin/Components/WriteWithAI/WriteWithAi.tsx
import React, { useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import WriteWithAIService from './WriteWithAI.service';
import UtilServiceReact from '../../../services/UtilServiceReact';

interface WriteWithAIProps {
    /** Identifier for the input field (e.g., 'jobTitle', 'jobDescription') */
    fieldName: string;
    /** Current value of the input (used if getInputCurrentValue is not provided) */
    currentValue?: string;
    /** Context of the feature provide content */
    context?: string;
    /** Callback function to update the parent component's state with the AI suggestion */
    onUpdate: (fieldName: string, newValue: string) => void;
    /** Optional: Function to get the current value from the input dynamically */
    getInputCurrentValue?: () => string; // <-- New prop
    /** Optional: Custom label for the button */
    label?: string | React.ReactNode;
    /** Optional: Custom CSS class */
    className?: string;
    /** Optional: Button variant */
    variant?: string;
    /** Optional: Button size */
    size?: 'sm' | 'lg';
}

const WriteWithAI: React.FC<WriteWithAIProps> = ({
    fieldName,
    currentValue = "",
    context = "",
    onUpdate,
    getInputCurrentValue, // <-- Destructure new prop
    label = <>✨ Write with AI</>,
    className = "",
    variant = "",
    size = 'sm'
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const { fetchAICompletion } = WriteWithAIService();
    const utilServiceReact = UtilServiceReact();

    const handleWriteWithAI = async () => {
        // Use the getter function if available, otherwise fall back to the prop
        const valueToProcess = getInputCurrentValue ? getInputCurrentValue() : currentValue;

        if (!valueToProcess || valueToProcess.trim() === "") {
            utilServiceReact.showWarningSimpleToast(
                'Please enter some text or keywords first to provide context for better AI suggestions.'
            );
            return;
        }
        setIsLoading(true);
        try {
            // Use valueToProcess for the API call
            const response = await fetchAICompletion(fieldName, valueToProcess, context);
            if (response?.suggestion) {
                onUpdate(fieldName, response.suggestion);
                utilServiceReact.showSuccessSimpleToast('AI suggestion applied!');
            } else {
                utilServiceReact.showErrorSimpleToast('AI Error: ' + (response?.message || 'Could not generate suggestion.'));
            }
        } catch (error: any) {
            console.error("AI Generation Error:", error);
            const errorMessage = error?.response?.data?.message || error.message || 'Failed to get AI suggestion.';
            utilServiceReact.showErrorSimpleToast('AI Error: ' + errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Button
            variant={variant}
            size={size}
            className={`px-2 ms-2 ${className}`}
            onClick={handleWriteWithAI}
            disabled={isLoading}
            title={`Generate ${fieldName} with AI`}
        >
            {isLoading ? (
                <>
                    <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-1"
                    />
                    Generating...
                </>
            ) : (
                label
            )}
        </Button>
    );
};

export default WriteWithAI;
